import { difference, intersection, union, xor, type MultiPolygon, type Polygon, type Ring } from "polygon-clipping";
import type { BooleanOperation } from "@/document/model/nodes";
import { boundsFromContours, contoursToPathData, pointInPolygon, signedArea, type Point } from "./pathGeometry";

export interface BooleanOperandContours {
  nodeId: string;
  sourceBounds?: { x: number; y: number; width: number; height: number };
  sourceKey?: string;
  path?: string;
  contours: Point[][];
}

export interface BooleanGeometryResult {
  contours: Point[][];
  path: string;
  bounds: { x: number; y: number; width: number; height: number };
}

const MIN_AREA = 0.001;
const POINT_EPSILON = 0.001;

export function resolveBooleanGeometry(operation: BooleanOperation, operands: BooleanOperandContours[]): BooleanGeometryResult {
  const geometries = operands
    .map(operand => contoursToMultiPolygon(operand.contours))
    .filter(geometry => geometry.length > 0);

  if (geometries.length === 0) {
    return emptyResult();
  }

  if (geometries.length === 1) {
    return resultFromMultiPolygon(geometries[0]!);
  }

  const [first, ...rest] = geometries;
  const result = operation === "union"
    ? union(first!, ...rest)
    : operation === "intersect"
      ? intersection(first!, ...rest)
      : operation === "subtract"
        ? difference(first!, ...rest)
        : xor(first!, ...rest);

  return resultFromMultiPolygon(result);
}

function contoursToMultiPolygon(contours: Point[][]): MultiPolygon {
  const rings = contours
    .map(normalizeContour)
    .filter(contour => contour.length >= 3 && Math.abs(signedArea(contour)) > MIN_AREA)
    .map(contour => ({
      contour,
      area: Math.abs(signedArea(contour)),
    }))
    .sort((left, right) => right.area - left.area);

  const polygons: Polygon[] = [];
  for (const ring of rings) {
    const depth = rings.filter(candidate => candidate !== ring && candidate.area > ring.area && pointInPolygon(interiorPoint(ring.contour), candidate.contour)).length;
    if (depth % 2 === 0) {
      polygons.push([pointsToRing(ring.contour)]);
      continue;
    }

    const parentIndex = nearestContainingPolygonIndex(polygons, ring.contour);
    if (parentIndex >= 0) {
      polygons[parentIndex]!.push(pointsToRing(ring.contour));
    }
  }

  return polygons.filter(polygon => polygon.length > 0);
}

function nearestContainingPolygonIndex(polygons: Polygon[], contour: Point[]): number {
  const point = interiorPoint(contour);
  let bestIndex = -1;
  let bestArea = Number.POSITIVE_INFINITY;
  for (let index = 0; index < polygons.length; index += 1) {
    const outer = polygons[index]?.[0];
    if (!outer) {
      continue;
    }

    const outerPoints = ringToPoints(outer);
    const area = Math.abs(signedArea(outerPoints));
    if (area < bestArea && pointInPolygon(point, outerPoints)) {
      bestIndex = index;
      bestArea = area;
    }
  }

  return bestIndex;
}

function resultFromMultiPolygon(multiPolygon: MultiPolygon): BooleanGeometryResult {
  const contours = multiPolygon
    .flatMap(polygon => polygon.map(ringToPoints))
    .map(normalizeContour)
    .filter(contour => contour.length >= 3 && Math.abs(signedArea(contour)) > MIN_AREA);

  return {
    contours,
    path: contoursToPathData(contours),
    bounds: boundsFromContours(contours),
  };
}

function emptyResult(): BooleanGeometryResult {
  return { contours: [], path: "", bounds: { x: 0, y: 0, width: 0, height: 0 } };
}

function normalizeContour(contour: Point[]): Point[] {
  const result: Point[] = [];
  for (const point of contour) {
    const previous = result.at(-1);
    if (!previous || distance(previous, point) > POINT_EPSILON) {
      result.push({ x: point.x, y: point.y });
    }
  }

  const first = result[0];
  const last = result.at(-1);
  if (first && last && distance(first, last) <= POINT_EPSILON) {
    result.pop();
  }

  return result;
}

function pointsToRing(points: Point[]): Ring {
  return points.map(point => [point.x, point.y]);
}

function ringToPoints(ring: Ring): Point[] {
  const points = ring.map(([x, y]) => ({ x, y }));
  const first = points[0];
  const last = points.at(-1);
  return first && last && distance(first, last) <= POINT_EPSILON ? points.slice(0, -1) : points;
}

function interiorPoint(points: Point[]): Point {
  const centroid = polygonCentroid(points);
  if (pointInPolygon(centroid, points)) {
    return centroid;
  }

  for (let index = 0; index < points.length; index += 1) {
    const a = points[index]!;
    const b = points[(index + 1) % points.length]!;
    const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const candidate = { x: (midpoint.x + centroid.x) / 2, y: (midpoint.y + centroid.y) / 2 };
    if (pointInPolygon(candidate, points)) {
      return candidate;
    }
  }

  return points[0] ?? centroid;
}

function polygonCentroid(points: Point[]): Point {
  let area = 0;
  let x = 0;
  let y = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    const factor = current.x * next.y - next.x * current.y;
    area += factor;
    x += (current.x + next.x) * factor;
    y += (current.y + next.y) * factor;
  }

  if (Math.abs(area) <= Number.EPSILON) {
    return {
      x: points.reduce((sum, point) => sum + point.x, 0) / Math.max(1, points.length),
      y: points.reduce((sum, point) => sum + point.y, 0) / Math.max(1, points.length),
    };
  }

  return { x: x / (3 * area), y: y / (3 * area) };
}

function distance(left: Point, right: Point): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}
